import { Box, Tab, Tabs, Typography, Paper, Stack, alpha } from '@mui/material';
import { useEffect } from 'react';
import { withRouter } from 'react-router';
import useSWR from 'swr';

// Helpers & Components
import AllResult from './result-list/AllResult';
import SubmittedResult from './result-list/SubmittedResult';
import RejectedResult from './result-list/RejectedResult';
import { GetRejectedReportCount, GetSubmittedReportCount } from '../../../helpers/api';
import { userCats } from '../../../helpers/appstate';

// Icons
import {
  AnalyticsOutlined,
  AssignmentLateOutlined,
  AssignmentTurnedInOutlined,
} from '@mui/icons-material';

const ResultScreen = withRouter(({ props, history }) => {
  const { userCategory } = props;
  
  // URL Parameter Handling
  const searchParams = new URLSearchParams(props?.location?.search);
  const currentTab = searchParams.get('tab') || 'all_results';

  // --- API FETCHERS ---
  const fetchSubmittedResultCount = async () => {
    const res = await GetSubmittedReportCount();
    const { data, status } = res?.result || {};
    if (status?.code === '00') return data;
    return 0;
  };

  const fetchRejectedResultCount = async () => {
    const res = await GetRejectedReportCount();
    const { data, status } = res?.result || {};
    if (status?.code === '00') return data;
    return 0;
  };

  // --- SWR POLLING ---
  const { data: submittedResultCount } = useSWR(
    'submitted-report-count',
    fetchSubmittedResultCount,
    { refreshInterval: 5000, revalidateOnFocus: false }
  );

  const { data: rejectedResultCount } = useSWR(
    'rejected-report-count',
    fetchRejectedResultCount,
    { refreshInterval: 5000, revalidateOnFocus: false }
  );

  // --- TAB CONFIGURATION ---
  const tabs = [
    {
      value: 'all_results',
      label: 'All Results',
      icon: <AnalyticsOutlined sx={{ fontSize: 18 }} />,
      component: () => <AllResult props={props} history={history} />,
    },
    {
      value: 'submitted_results',
      label: 'Submitted',
      icon: <AssignmentTurnedInOutlined sx={{ fontSize: 18 }} />,
      component: () => <SubmittedResult props={props} history={history} />,
      count: submittedResultCount,
      countColor: 'primary',
    },
    {
      value: 'rejected_results',
      label: 'Rejected',
      icon: <AssignmentLateOutlined sx={{ fontSize: 18 }} />,
      component: () => <RejectedResult props={props} history={history} />,
      count: rejectedResultCount,
      countColor: 'error',
    },
  ];

  // --- LOGIC: DEFAULT TAB INITIALIZATION ---
  useEffect(() => {
    const tabInUrl = searchParams.get('tab');
    if (!tabInUrl) {
      const newParam = new URLSearchParams(props.location.search);
      newParam.set('tab', 'all_results');
      // Use replace to ensure the back button works correctly
      props.history.replace({ search: newParam.toString() });
    }
  }, []);

  // Calculate the index for the MUI Tabs component
  const tabIndex = tabs.findIndex((x) => x.value === currentTab);
  const activeIndex = tabIndex === -1 ? 0 : tabIndex;

  const handleTabChange = (event, newValue) => {
    const newParam = new URLSearchParams(props.location.search);
    newParam.set('tab', tabs[newValue].value);
    props.history.push({ search: newParam.toString() });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#F4F6F8', minHeight: '100vh', mt: -1 }}>
      
      {/* PAGE HEADER SECTION */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" fontWeight={700} color="text.primary" gutterBottom>
          Monitoring & QAQC Results
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Review, analyze, and manage your facility monitoring and QAQC data reports.
        </Typography>
      </Box>

      {/* TAB NAVIGATION AREA (ONLY FOR EDM) */}
      {userCategory === userCats.EDM ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: '16px',
            bgcolor: 'transparent',
            mb: 3,
          }}
        >
          <Tabs
            value={activeIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
              '& .MuiTab-root': {
                minHeight: 64,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.9rem',
                color: 'text.secondary',
                transition: 'all 0.2s',
                '&.Mui-selected': {
                  color: 'primary.main',
                },
                '&:hover': {
                  color: 'primary.light',
                  bgcolor: alpha('#1976d2', 0.02),
                },
              },
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            {tabs.map((tabItem, idx) => (
              <Tab
                key={tabItem.value}
                icon={tabItem.icon}
                iconPosition="start"
                label={
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Typography variant="inherit">{tabItem.label}</Typography>
                    {tabItem.count > 0 && (
                      <Box
                        sx={{
                          px: 1,
                          py: 0.2,
                          borderRadius: '6px',
                          bgcolor: tabItem.countColor === 'error' 
                            ? alpha('#d32f2f', 0.1) 
                            : alpha('#1976d2', 0.1),
                          color: tabItem.countColor === 'error' ? 'error.main' : 'primary.main',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                        }}
                      >
                        {tabItem.count}
                      </Box>
                    )}
                  </Stack>
                }
              />
            ))}
          </Tabs>
        </Paper>
      ) : null}

      {/* RENDER ACTIVE TAB COMPONENT */}
      <Box>
        {tabs[activeIndex]?.component()}
      </Box>
    </Box>
  );
});

export default ResultScreen;